// What the operator's home says about the andons at this work centre.
//
// Two rows, from one payload. The floor read (`andonApi.floor('all')`) carries
// every open andon and every one settled in the last day; this picks the ones
// about the station the device is pointed at and sorts them into the two
// lines the frame draws:
//
//   open     "Open at this work centre" — one quiet row each, with who has it
//            and when they said they would be there
//   sorted   "Sorted by Dean · 10:31" — one line each, for the day's closes,
//            so an operator coming back from a break can see the andon they
//            raised was answered rather than wondering whether it vanished
//
// WHY THE STATION IS MATCHED BY NAME. The andon payload names the work centre
// and never carries its id — the Andon app's screens had no station ids to
// compare against. Both names come from the same `mrp.workcenter` rows, so
// the match is exact; it is written as a comparison of trimmed strings rather
// than ids because that is what the data offers, not because ids would be
// worse.
//
// "TODAY" IS THE LOCAL CALENDAR DAY, not the last twenty-four hours. The
// server keeps a settled andon for a day; what the operator wants to see is
// this shift's, and a close from 23:50 last night under this morning's list
// reads as something still going on. The end of the shift is the day's end —
// the shop floor has no shift calendar on the client, and the device's day is
// the nearest honest thing to it.

import type { AndonAlertVM } from '@/api/andon/types';
import { startOfDay } from '@/lib/time';

export interface HomeAndonRows {
  /** Open at this station, oldest first — the one waiting longest leads. */
  open: AndonAlertVM[];
  /** Closed at this station today, newest first — the latest close leads. */
  sorted: AndonAlertVM[];
}

/**
 * How many whole minutes nobody has had it past the acknowledge target.
 *
 * The frame's `overBy` (script L781), drawn on the home row as "Nobody yet ·
 * 4 min over": only on an open andon nobody holds, and only once the clock
 * has run past the target — null otherwise, so a row that is merely waiting
 * says "Nobody yet" and no more. Minutes, floored: "0 min over" is not a
 * thing anybody says.
 */
export function overBy(a: Pick<AndonAlertVM, 'open' | 'owner' | 'sla'>, now: number): number | null {
  if (!a.open || !a.owner.unowned || a.sla.dueAt == null) return null;
  const minutes = Math.floor((now - a.sla.dueAt) / 60_000);
  return minutes > 0 ? minutes : null;
}

export function homeAndonRows(alerts: readonly AndonAlertVM[], workcentreName: string | null, now: number): HomeAndonRows {
  const here = (workcentreName ?? '').trim();
  if (!here) return { open: [], sorted: [] };
  const mine = alerts.filter((a) => a.workcentre.trim() === here);
  const today = startOfDay(now);
  return {
    open: mine.filter((a) => a.open).sort((a, b) => a.raisedAt - b.raisedAt),
    // Closed, not cancelled: a withdrawn andon was never sorted by anybody,
    // and a line saying "Sorted by —" about it would be a false record on the
    // one screen every operator looks at.
    sorted: mine
      .filter((a) => a.stage === 'closed' && a.closedAt != null && a.closedAt >= today)
      .sort((a, b) => (b.closedAt ?? 0) - (a.closedAt ?? 0)),
  };
}
